import { useAccess } from "~/access/useAccess";
import { useApiQuery, useCurrentLocation, useOrgs } from "~/hooks";
import { MILLISECONDS } from "~/utils/Constants";
import { LooseObject } from "~/utils/Types";
import { IN_PAGE_IDS } from "~/utils/navManifest";

export type DatasetTaggingTags = {
  [key: string]: string | number | boolean;
};

type DatasetTaggingUserSettings = {
  overrides: string[];
  tags: string[];
};

/**
 * Dataset-tagging state for one image: which traits a labeller may override,
 * which boolean tags exist, and the values already recorded against this
 * carcase.
 *
 * `isEnabled` alone is not enough to render the tagging UI — a location can
 * grant the section with neither overrides nor tags configured, which is the
 * same as having nothing to tag.
 */
const useDatasetTagging = (item: LooseObject | undefined) => {
  const { getOrgShortName } = useOrgs();
  const { canSee } = useAccess();
  const location = useCurrentLocation();

  const isEnabled = canSee(IN_PAGE_IDS.datasetTagging);

  const userSettingsQuery = useApiQuery<DatasetTaggingUserSettings>({
    config: {
      data: {
        org: getOrgShortName(),
        location: location,
      },
      method: "post",
    },
    options: {
      staleTime: 5 * MILLISECONDS.MINUTE,
      enabled: isEnabled,
    },
    path: "/user/meq/dataset-tagging/user-settings",
  });

  const tagsQuery = useApiQuery<DatasetTaggingTags[]>({
    config: {
      data: {
        org: getOrgShortName(),
        location: location,
        query: {
          dataset: "camera",
          origins: [
            {
              barcode: item?.BARCODE,
              companyLocation: location,
              meatType: item?.MEAT_TYPE,
              photoDateTime: item?.PHOTO_DATETIME,
            },
          ],
        },
      },
      method: "post",
    },
    options: {
      enabled: isEnabled,
    },
    path: "/user/meq/dataset-tagging/tags",
  });

  const overrides = userSettingsQuery.data?.overrides || [];
  const tags = userSettingsQuery.data?.tags || [];

  return {
    isEnabled: isEnabled && (overrides.length > 0 || tags.length > 0),
    overrides,
    tags,
    tagsResult:
      Array.isArray(tagsQuery.data) && tagsQuery.data.length > 0
        ? tagsQuery.data[0]
        : undefined,
  };
};

export default useDatasetTagging;
